import { api } from '@/lib/api'
import { getToken } from '@/lib/auth'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

export async function subscribeToPush(): Promise<{ success: boolean; error?: string }> {
  if (!pushSupported()) return { success: false, error: 'Push notifications are not supported in this browser.' }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return { success: false, error: 'Notification permission was not granted.' }

  const { publicKey } = await api.pushVapidPublicKey()
  if (!publicKey) return { success: false, error: 'Server has no VAPID key configured.' }

  const reg = await navigator.serviceWorker.ready
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  })

  await api.pushSubscribe(subscription.toJSON())
  return { success: true }
}

export async function unsubscribeFromPush(): Promise<void> {
  const sub = await getExistingSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  await api.pushUnsubscribe(endpoint).catch(() => {})
}

// The service worker can't read localStorage directly, so the main app
// hands it the current auth token whenever the app loads (or the token
// changes) -- cached there for the notification action buttons to use
// later, possibly while the app itself isn't open at all.
export async function syncAuthTokenToServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const token = getToken()
  if (!token) return
  const reg = await navigator.serviceWorker.ready
  reg.active?.postMessage({ type: 'AUTH_TOKEN', token })
}
